/**
 * Segment chips and "This belongs to…" (docs/ux/project-tab-spec.md §1.3a, Phase 3
 * `segmentation`). Chips never block: the agent already answered in place; a chip only offers
 * the split (*New topic?*, *Continue this, or start a new item?*, or the out-of-scope chip on a
 * direct steer). "This belongs to…" moves a message (and optionally the later ones) into another
 * item; both items record the move.
 */
import type { BarkRecord, BarkThreadMessage } from '@viko/protocol'
import { useState } from 'react'
import type { BarkStore } from '../../lib/bark-store.js'

type Run = (fn: () => Promise<unknown>) => Promise<void>
const newItemOf = (r: unknown) =>
  (r as { result?: { newItemId?: string } } | undefined)?.result?.newItemId

/** The user message a chip is about: `sug-<id>` / `con-<id>`; a direct steer's chip → its words. */
export function chipMessageId(bark: BarkRecord, chip: BarkThreadMessage): string | undefined {
  const m = /^(?:sug|con)-(.+)$/.exec(chip.id)
  if (m) return m[1]
  return bark.source === 'direct' ? bark.thread[0]?.id : undefined
}

export function SegmentChip({
  bark,
  chip,
  store,
  busy,
  run,
  onOpenItem,
}: {
  bark: BarkRecord
  chip: BarkThreadMessage
  store: BarkStore
  busy: boolean
  run: Run
  onOpenItem: (id: string) => void
}) {
  const [dismissed, setDismissed] = useState(false)
  const messageId = chipMessageId(bark, chip)
  const stillHere = !!messageId && bark.thread.some((m) => m.id === messageId)
  if (dismissed || !stillHere) return <span className="text-text-3">{chip.text}</span>
  const split = () =>
    run(async () => {
      const id = newItemOf(await store.control(bark, 'split', { messageId }))
      if (id) onOpenItem(id)
    })
  return (
    <span className="flex flex-wrap items-center gap-2" data-segment-chip={chip.segmentChip}>
      <span className="text-text-2">{chip.text}</span>
      <button
        type="button"
        className="text-accent-text"
        disabled={busy}
        onClick={() => void split()}
      >
        {chip.segmentChip === 'continue-or-new' ? 'Start a new item' : 'Split into new item'}
      </button>
      <button
        type="button"
        className="text-text-2 underline"
        disabled={busy}
        onClick={() => setDismissed(true)}
      >
        {chip.segmentChip === 'continue-or-new' ? 'Continue here' : 'Keep here'}
      </button>
    </span>
  )
}

export function BelongsTo({
  bark,
  message,
  items,
  store,
  busy,
  run,
  onOpenItem,
}: {
  bark: BarkRecord
  message: BarkThreadMessage
  items: { id: string; title: string }[]
  store: BarkStore
  busy: boolean
  run: Run
  onOpenItem: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState('')
  const [withLater, setWithLater] = useState(false)
  const others = items.filter((i) => i.id !== bark.id)
  if (!others.length) return null
  if (!open)
    return (
      <button
        type="button"
        className="text-[14px] text-text-3 opacity-70 group-hover:opacity-100 focus:opacity-100"
        disabled={busy}
        onClick={() => setOpen(true)}
      >
        This belongs to…
      </button>
    )
  return (
    <span className="flex flex-wrap items-center gap-2 text-[14px]" data-belongs-to>
      <select
        aria-label="Move this message to"
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        className="rounded-md border border-border-subtle bg-surface-1 px-2 py-1 text-[14px] text-text-1"
      >
        <option value="">Choose an item…</option>
        {others.map((i) => (
          <option key={i.id} value={i.id}>
            {i.title}
          </option>
        ))}
      </select>
      <label className="flex items-center gap-1 text-text-2">
        <input
          type="checkbox"
          checked={withLater}
          onChange={(e) => setWithLater(e.target.checked)}
        />
        and later messages
      </label>
      <button
        type="button"
        className="text-accent-text"
        disabled={busy || !target}
        onClick={() =>
          void run(async () => {
            await store.control(bark, 'move', {
              messageId: message.id,
              targetItemId: target,
              ...(withLater ? { withLater: true } : {}),
            })
            setOpen(false)
            onOpenItem(target)
          })
        }
      >
        Move
      </button>
      <button type="button" className="text-text-2 underline" onClick={() => setOpen(false)}>
        Close
      </button>
    </span>
  )
}
